/**
 * Portail membre : « Mes dépôts » (bleu) et « Mes allocations » (ambre) — index.css (.entrea-account-row / .entrea-demands-row).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { assistanceAPI } from '@/services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { getResolvedPortalTitle } from '../utils/portalBranding';
import { useModules } from '../contexts/ModulesContext';
import { usePermissions } from '../hooks/usePermissions';
import { trackDashboardEvent } from '../utils/analytics';
import { isBoardOfficerRole, isOrgAdminRole, normalizeRoleKeyForPermissions } from '../utils/iamRoles';
import { resolveMemberProfileId, resolveNextMemberProfileId } from '../utils/memberProfileId';
import {
  isAdhesionContributionMonth,
  memberRegularContributionsAmount,
} from '../utils/memberDashboardPersonalStats';
import {
  harmonizedMemberUnpaidMonthsDisplay,
  isArrearsCritical,
} from '../utils/contributionArrearsUi';
import UiSurface from './UiSurface';

/** Exclus de la grille mobile : déjà dans la barre du bas, héros, ou carte Ma caisse. */
const MOBILE_SERVICE_GRID_SKIP_HREFS = new Set([
  '/contributions',
  '/assistance',
  '/profile',
  '/my-fund',
  '/my-fund/financial',
  '/notifications',
  '/support',
]);

/** Tuiles visibles avant le bouton « Plus » (liens Administration desktop). */
/** Liens bureau visibles d’emblée (le reste sous « Plus »). */
const MANAGER_HUB_LINKS_VISIBLE = 4;

/** Raccourcis grille « Services » (mobile) — même logique modules / droits que l’accès rapide bureau. */
const MEMBER_SERVICE_ITEMS = [
  {
    nameKey: 'nav.contributions',
    href: '/contributions',
    icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
    permission: ['contributions.view', 'contributions.view_own'],
    moduleRoute: '/contributions',
  },
  {
    nameKey: 'nav.allocations',
    href: '/assistance',
    icon: 'M12 8c-2.21 0-4 1.79-4 4v1H6a2 2 0 00-2 2v1h16v-1a2 2 0 00-2-2h-2v-1c0-2.21-1.79-4-4-4zm-2 5v-1a2 2 0 114 0v1h-4z',
    permission: ['contributions.view', 'contributions.view_own'],
    moduleRoute: '/support',
  },
  {
    nameKey: 'nav.members',
    href: '/members',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    permission: ['members.view', 'members.view_own'],
    moduleRoute: '/members',
  },
  {
    nameKey: 'memberPortal.myOrganization',
    href: '/my-fund',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    permission: ['financial.view', 'financial.view_own'],
    moduleRoute: '/financial',
  },
  {
    nameKey: 'memberPortal.ledgeraShort',
    href: '/my-fund/financial',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    permission: ['financial.view', 'financial.view_own'],
    moduleRoute: '/financial',
  },
  {
    nameKey: 'nav.documents',
    href: '/documents',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    permission: ['documents.view', 'documents.view_public'],
    moduleRoute: '/documents',
  },
  {
    nameKey: 'nav.calendar',
    href: '/calendar',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    permission: ['events.view', 'events.view_public'],
    moduleRoute: '/calendar',
  },
  {
    nameKey: 'memberPortal.meetingMinutes',
    href: '/meetings',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    permission: ['meetings.view', 'meetings.view_public'],
    moduleRoute: '/meeting-minutes',
  },
  {
    nameKey: 'memberPortal.profile',
    href: '/profile',
    icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    permission: ['dashboard.view'],
    moduleRoute: null,
  },
  {
    nameKey: 'memberPortal.announcements',
    href: '/notifications',
    icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    permission: ['dashboard.view'],
    moduleRoute: '/notifications',
  },
  {
    nameKey: 'memberPortal.support',
    href: '/support',
    icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4v-4z',
    permission: ['dashboard.view'],
    moduleRoute: '/support',
  },
];

function normCaseStatus(s) {
  return String(s || '').toLowerCase().trim();
}

function caseStatusLabel(t, s) {
  const k = normCaseStatus(s);
  if (!k) return t('memberDashboard.dash');
  return t(`memberDashboard.caseStatus.${k}`, { defaultValue: k });
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Sous-titre compte : N° membre si dispo, sinon id technique. */
function memberAccountSubtitle(stats, t) {
  if (!stats || typeof stats !== 'object') return t('memberDashboard.accountGeneric');
  const mn = String(stats.member_number || '').trim();
  if (mn) return t('memberDashboard.accountWithNumber', { number: mn });
  const id = String(stats.member_id || stats.person_id || '').trim();
  return id ? t('memberDashboard.accountWithId', { id }) : t('memberDashboard.accountGeneric');
}

/** Nom complet affiché (fiche membre prioritaire, puis compte IAM). */
function resolveMemberFullDisplayName(stats, user, t) {
  const fromStats = String(stats?.member_name || stats?.name || '').trim();
  if (fromStats) return fromStats;
  const profile = stats?.metadata?.profile || stats?.profile || {};
  const composed = [profile.first_name, profile.middle_name, profile.last_name]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .join(' ');
  if (composed) return composed;
  const fromUser = String(user?.name || user?.full_name || user?.display_name || '').trim();
  if (fromUser) return fromUser;
  return t('memberPortal.member');
}

/** Mois court + année (YYYY-MM des cotisations classiques). */
function formatMonthYearFromYm(ym, localeFmt) {
  if (!/^\d{4}-\d{2}$/.test(String(ym || '').trim())) return null;
  try {
    return new Date(`${String(ym).trim()}-01T12:00:00`).toLocaleDateString(localeFmt, {
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}
function addOneMonthToYm(ym) {
  const s = String(ym || '').trim();
  if (!/^\d{4}-\d{2}$/.test(s)) return null;
  const [y, m] = s.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatDepotLineFromRow(row, localeFmt, t) {
  const raw = row.paid_at || row.updated_at || row.created_at;
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const rest = d.toLocaleDateString(localeFmt, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return t('memberDashboard.depositLine', { date: rest });
  } catch {
    return null;
  }
}

function contributionHistorySortTime(row) {
  const raw = row.paid_at || row.updated_at || row.created_at;
  if (raw) {
    const t = new Date(raw).getTime();
    if (Number.isFinite(t)) return t;
  }
  const m = String(row.month || '').trim();
  if (/^\d{4}-\d{2}$/.test(m)) {
    return new Date(`${m}-01T12:00:00`).getTime();
  }
  return 0;
}

/** Colonne date « Activité récente » : mois calendaire ou date réelle pour les lignes `month: adhesion`. */
function formatRecentActivityPeriod(item, t, localeFmt) {
  const monthStr = String(item?.month || '').trim();
  if (/^\d{4}-\d{2}$/.test(monthStr)) {
    return formatMonthYearFromYm(monthStr, localeFmt) || monthStr;
  }
  const raw = item?.paid_at || item?.updated_at || item?.created_at;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(localeFmt, { month: 'short', year: 'numeric' });
    }
  }
  if (isAdhesionContributionMonth(monthStr)) {
    return t('memberDashboard.adhesionShort');
  }
  return monthStr || t('memberDashboard.dash');
}

function contributionHistoryKindLabel(monthRaw, isPaid, t) {
  const m = String(monthRaw || '').trim();
  if (isAdhesionContributionMonth(m)) {
    return isPaid ? t('memberDashboard.kindAdhesionPaid') : t('memberDashboard.kindAdhesionUnpaid');
  }
  return isPaid ? t('memberDashboard.kindDuePaid') : t('memberDashboard.kindDueUnpaid');
}

function contributionHistoryDescription(monthRaw, isPaid, t, localeFmt) {
  const m = String(monthRaw || '').trim();
  let period;
  if (/^\d{4}-\d{2}$/.test(m)) {
    period = formatMonthYearFromYm(m, localeFmt) || m;
  } else if (isAdhesionContributionMonth(m)) {
    period = t('memberDashboard.adhesionFees');
  } else {
    period = m || t('memberDashboard.period');
  }
  return isPaid
    ? t('memberDashboard.descWithStatusPaid', { period })
    : t('memberDashboard.descWithStatusUnpaid', { period });
}

function eventCategoryRowLabel(t, key, sample, mechanismById) {
  const k = String(key || '').trim();
  if (k && k !== '_autre') {
    return t(`memberDashboard.eventCategory.${k}`, {
      defaultValue: k.charAt(0).toUpperCase() + k.slice(1),
    });
  }
  const mid = String(sample?.mechanism_id || '').trim();
  if (mid && mechanismById[mid]?.name) return String(mechanismById[mid].name).trim();
  return t('memberDashboard.eventCategoryOther');
}

const DASH_ASSISTANCE_LIMIT = 100;

/** Montants versés / accordés (hors demande en cours) — agrégation par type d’allocation. */
function addCaseToReleasedAmount(agg, c) {
  const st = normCaseStatus(c.status);
  if (st === 'closed') {
    const paid = num(c.paid_amount);
    const appr = num(c.approved_amount);
    agg.amount += paid > 0 ? paid : appr;
  } else if (st === 'approved' || st === 'paid') {
    agg.amount += num(c.approved_amount);
  }
}

const MemberDashboard = ({
  personalStats,
  globalStats,
  internalActivities = [],
  /** Dossiers assistance déjà chargés côté Home (membre). */
  assistanceDataFromParent = false,
  /** Ignoré si `assistanceDataFromParent` est false. */
  assistanceCases: assistanceCasesFromParent = [],
  /** Chargement API assistance lorsque `assistanceDataFromParent` est true. */
  assistanceParentLoading = false,
}) => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { user } = useAuth();
  const { settings } = useSettings();
  const organizationBrandName = getResolvedPortalTitle(settings);
  const { hasPermission, role } = usePermissions();
  const { isRouteEnabled } = useModules();
  const [mechanismById, setMechanismById] = useState({});
  const [expandedAccountRow, setExpandedAccountRow] = useState(null);
  const [expandedAssistanceKey, setExpandedAssistanceKey] = useState(null);
  const [localAssistanceCases, setLocalAssistanceCases] = useState([]);
  const [localAssistanceLoading, setLocalAssistanceLoading] = useState(false);
  const useParentAssistance = assistanceDataFromParent;
  const assistanceCases = useParentAssistance ? assistanceCasesFromParent : localAssistanceCases;
  const assistanceLoading = useParentAssistance ? assistanceParentLoading : localAssistanceLoading;
  /** Horloge tableau de bord (date + heure visibles, mise à jour périodique). */
  const [dashboardClock, setDashboardClock] = useState(() => new Date());
  const [managerLinksExpanded, setManagerLinksExpanded] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setDashboardClock(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const contributionHistory = Array.isArray(personalStats?.contribution_history)
    ? personalStats.contribution_history
    : [];
  const unpaidContributions = Array.isArray(personalStats?.unpaid_contributions)
    ? personalStats.unpaid_contributions
    : [];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await assistanceAPI.getMechanisms({ limit: 200, skip: 0 });
        const list = res?.data?.solidarity_mechanisms || [];
        const map = {};
        for (const m of list) {
          if (m?.id) map[m.id] = m;
        }
        if (!cancelled) setMechanismById(map);
      } catch {
        if (!cancelled) setMechanismById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (assistanceDataFromParent) {
      return undefined;
    }
    const mid = resolveMemberProfileId(personalStats, user);
    if (!mid) {
      setLocalAssistanceCases([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLocalAssistanceLoading(true);
        const res = await assistanceAPI.getCases({
          limit: DASH_ASSISTANCE_LIMIT,
          skip: 0,
          member_profile_id: mid,
        });
        let rows = res?.data?.assistance_cases || [];
        if (!cancelled && rows.length === 0) {
          const alt = resolveNextMemberProfileId(personalStats, user, mid);
          if (alt) {
            try {
              const res2 = await assistanceAPI.getCases({
                limit: DASH_ASSISTANCE_LIMIT,
                skip: 0,
                member_profile_id: alt,
              });
              rows = res2?.data?.assistance_cases || [];
            } catch {
              /* ignore */
            }
          }
        }
        if (!cancelled) {
          setLocalAssistanceCases(rows);
        }
      } catch {
        if (!cancelled) setLocalAssistanceCases([]);
      } finally {
        if (!cancelled) setLocalAssistanceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    assistanceDataFromParent,
    personalStats?.member_id,
    personalStats?.person_id,
    user?.member_id,
    user?.person_id,
  ]);

  const assistanceStats = useMemo(() => {
    const list = Array.isArray(assistanceCases) ? assistanceCases : [];
    const pending = new Set(['draft', 'submitted', 'in_review']);
    let pendingCount = 0;
    let amountSubmitted = 0;
    let amountApproved = 0;
    let amountClosed = 0;
    for (const c of list) {
      const st = normCaseStatus(c.status);
      if (pending.has(st)) {
        pendingCount += 1;
        amountSubmitted += num(c.requested_amount);
      } else if (st === 'closed') {
        const paid = num(c.paid_amount);
        const appr = num(c.approved_amount);
        amountClosed += paid > 0 ? paid : appr;
      } else if (st === 'approved' || st === 'paid') {
        amountApproved += num(c.approved_amount);
      }
    }
    const amountFinalized = amountApproved + amountClosed;
    return {
      pendingCount,
      amountSubmitted,
      amountApproved,
      amountClosed,
      amountFinalized,
      totalCases: list.length,
    };
  }, [assistanceCases]);

  /** Sous-titre sous le total allocations (suivi des dossiers en cours). */
  const assistanceDossierSummary = useMemo(() => {
    if (assistanceLoading) return t('memberDashboard.assistanceLoading');
    if (assistanceStats.totalCases === 0) return t('memberDashboard.assistanceNone');
    if (assistanceStats.pendingCount === 0 && assistanceStats.totalCases > 0) {
      return t('memberDashboard.assistanceNoPending');
    }
    return t('memberDashboard.assistancePending', { count: assistanceStats.pendingCount });
  }, [assistanceLoading, assistanceStats, t]);

  /** Masquer toute la zone « Mes allocations » tant que le total versé est 0 (après chargement). */
  const showAllocationsDashboard = useMemo(() => {
    if (assistanceLoading) return true;
    return Number(assistanceStats.amountFinalized || 0) > 0;
  }, [assistanceLoading, assistanceStats.amountFinalized]);

  const allocationRowsByType = useMemo(() => {
    const list = Array.isArray(assistanceCases) ? assistanceCases : [];
    const byKey = new Map();
    for (const c of list) {
      const raw = String(c.event_category || '').trim().toLowerCase();
      const key = raw || '_autre';
      if (!byKey.has(key)) {
        byKey.set(key, { amount: 0, sample: c, cases: [] });
      }
      const bucket = byKey.get(key);
      addCaseToReleasedAmount(bucket, c);
      bucket.cases.push(c);
    }
    const rows = Array.from(byKey.entries()).map(([key, b]) => ({
      key: `alloc-${key}`,
      typeLabel: eventCategoryRowLabel(t, key, b.sample, mechanismById),
      amount: b.amount,
      cases: Array.isArray(b.cases) ? b.cases : [],
    }));
    rows.sort((a, b) => a.typeLabel.localeCompare(b.typeLabel, localeFmt));
    return rows;
  }, [assistanceCases, mechanismById, t, localeFmt]);

  const { recentAdhesionTransactions, recentRegularTransactions } = useMemo(() => {
    const list = Array.isArray(contributionHistory) ? contributionHistory : [];
    const adh = list.filter((r) => isAdhesionContributionMonth(r.month));
    const reg = list.filter((r) => !isAdhesionContributionMonth(r.month));

    const build = (rows, mode) => {
      const sorted = [...rows].sort((a, b) => contributionHistorySortTime(b) - contributionHistorySortTime(a));
      return sorted.slice(0, 3).map((row, idx) => {
        const status = String(row.status || '').toLowerCase();
        const isPaid = ['paid', 'approved', 'completed'].includes(status);
        const monthStr = String(row.month || '').trim();
        const isCalendar = /^\d{4}-\d{2}$/.test(monthStr);
        const depotLine = formatDepotLineFromRow(row, localeFmt, t) || t('memberDashboard.depositShort');
        let titleBold;
        if (mode === 'adhesion') {
          titleBold = isPaid ? t('memberDashboard.titleAdhesionFees') : t('memberDashboard.titleAdhesionFeesPending');
        } else if (isCalendar) {
          titleBold = isPaid ? t('memberDashboard.titleMonthlyPaid') : t('memberDashboard.titleMonthlyPending');
        } else {
          titleBold = isPaid ? t('memberDashboard.titleGenericPaid') : t('memberDashboard.titleGenericPending');
        }
        const txRef = String(row.id || row._id || '').trim() || t('memberDashboard.dash');
        const periodPart = isCalendar
          ? formatMonthYearFromYm(monthStr, localeFmt) || ''
          : mode === 'adhesion'
            ? t('memberDashboard.adhesionMeta')
            : String(monthStr || t('memberDashboard.dash'));
        const metaLine = `${txRef}, ${titleBold} ${periodPart}`.replace(/\s+$/, '');

        return {
          id: `tx-${mode}-${idx}-${row.id || row.month || idx}`,
          depotLine,
          metaLine,
          amount: Number(row.amount || 0),
          isPaid,
        };
      });
    };
    return {
      recentAdhesionTransactions: build(adh, 'adhesion'),
      recentRegularTransactions: build(reg, 'regular'),
    };
  }, [contributionHistory, localeFmt, t]);

  /** Dernier mois calendaire cotisé (hors adhésion) + échéance mois suivant, pour le détail Cotisations. */
  const cotisationCalMeta = useMemo(() => {
    const list = Array.isArray(contributionHistory) ? contributionHistory : [];
    const paid = new Set(['completed', 'paid', 'approved']);
    let maxYm = '';
    for (const c of list) {
      if (!paid.has(String(c.status || '').toLowerCase())) continue;
      if (isAdhesionContributionMonth(c.month)) continue;
      const m = String(c.month || '').trim();
      if (/^\d{4}-\d{2}$/.test(m) && m > maxYm) maxYm = m;
    }
    let lastLabel = maxYm ? formatMonthYearFromYm(maxYm, localeFmt) : null;
    let nextLabel = null;
    if (maxYm) {
      const nym = addOneMonthToYm(maxYm);
      nextLabel = nym ? formatMonthYearFromYm(nym, localeFmt) : null;
    } else {
      const now = new Date();
      const n = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      nextLabel = n.toLocaleDateString(localeFmt, { month: 'short', year: 'numeric' });
    }
    if (!lastLabel && personalStats?.last_payment) {
      try {
        lastLabel = new Date(personalStats.last_payment).toLocaleDateString(localeFmt, {
          month: 'short',
          year: 'numeric',
        });
      } catch {
        lastLabel = null;
      }
    }
    if (!nextLabel) {
      const now = new Date();
      const n = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      nextLabel = n.toLocaleDateString(localeFmt, { month: 'short', year: 'numeric' });
    }
    return {
      dernierPaiement: lastLabel || t('memberDashboard.dash'),
      prochainPaiement: nextLabel || t('memberDashboard.dash'),
    };
  }, [contributionHistory, personalStats?.last_payment, localeFmt, t]);

  const arrearsMonths = useMemo(
    () => (personalStats ? harmonizedMemberUnpaidMonthsDisplay(personalStats) : 0),
    [personalStats]
  );
  const arrearsCritical = arrearsMonths > 0 && isArrearsCritical(arrearsMonths);

  const memberServices = useMemo(() => {
    return MEMBER_SERVICE_ITEMS.filter((item) => {
      if (MOBILE_SERVICE_GRID_SKIP_HREFS.has(item.href)) return false;
      const perms = item.permission;
      if (perms && perms.length > 0) {
        const ok = perms.some((p) => hasPermission(p));
        if (!ok) return false;
      }
      const mod = item.moduleRoute;
      if (mod) {
        return isRouteEnabled(mod);
      }
      return true;
    });
  }, [hasPermission, isRouteEnabled, role]);

  const canSeeOrgFund =
    (hasPermission('financial.view') || hasPermission('financial.view_own')) &&
    isRouteEnabled('/financial');

  const orgFundSnapshot = useMemo(() => {
    const g = globalStats;
    if (!g || typeof g !== 'object') return null;
    const hasAny =
      g.total_balance != null ||
      g.total_income != null ||
      g.total_expenses != null ||
      g.total_members != null;
    if (!hasAny) return null;
    return {
      balance: num(g.total_balance),
      income: num(g.total_income),
      expenses: num(g.total_expenses),
      members: num(g.total_members),
      activeMembers: num(g.active_members),
      contributions: num(g.total_contributions),
      pendingContributions: num(g.pending_contributions_amount ?? g.pending_contributions),
    };
  }, [globalStats]);

  const showOrgFundOnHome = canSeeOrgFund && !!orgFundSnapshot;

  const managerHubLinks = useMemo(() => {
    /**
     * Le rôle d'affichage du hub gestionnaire prend en compte la position projet :
     * si l'utilisateur a une position (`position_role_keys`) qui contient un rôle bureau,
     * on adopte celui-ci pour piloter les liens — même si `user.role` est resté
     * un rôle technique (ex. `participant`).
     */
    const baseRaw = normalizeRoleKeyForPermissions(user?.role || role);
    const positionKeys = Array.isArray(user?.position_role_keys) ? user.position_role_keys : [];
    let resolvedRole = baseRaw;
    if (!isBoardOfficerRole(resolvedRole)) {
      for (const k of positionKeys) {
        const norm = normalizeRoleKeyForPermissions(k);
        if (isBoardOfficerRole(norm)) {
          resolvedRole = norm;
          break;
        }
      }
    }
    const rawRole = resolvedRole;
    const isOfficer = isBoardOfficerRole(rawRole);
    if (!isOfficer) return [];
    const links = [];
    if (rawRole === 'executive') {
      links.push(
        { label: t('nav.contributions', { defaultValue: 'Cotisations' }), href: '/portal/contributions', route: '/contributions' },
        {
          label: t('nav.approvals', { defaultValue: 'Approbations' }),
          href: '/notifications?tab=approvals',
          route: '/notifications',
        },
        { label: t('nav.members', { defaultValue: 'Membres' }), href: '/portal/members', route: '/members' },
        { label: t('nav.dashboard', { defaultValue: 'Tableau de bord' }), href: '/portal', route: '/admin' },
        { label: t('nav.allocations', { defaultValue: 'Allocations' }), href: '/portal/assistance', route: '/support' },
        { label: t('nav.users', { defaultValue: 'Utilisateurs' }), href: '/portal/users', route: '/users' },
        { label: t('nav.settings', { defaultValue: 'Parametres' }), href: '/portal/settings', route: '/settings' }
      );
    } else if (rawRole === 'finance_lead') {
      links.push(
        { label: t('nav.contributions', { defaultValue: 'Cotisations' }), href: '/portal/contributions', route: '/contributions' },
        {
          label: t('nav.approvals', { defaultValue: 'Approbations' }),
          href: '/notifications?tab=approvals',
          route: '/notifications',
        },
        { label: t('nav.dashboard', { defaultValue: 'Tableau de bord' }), href: '/portal', route: '/admin' },
        { label: t('nav.allocations', { defaultValue: 'Allocations' }), href: '/portal/assistance', route: '/support' },
        { label: t('memberPortal.ledgeraMovements', { defaultValue: 'Mouvements Ledgera' }), href: '/portal/ledgera', route: '/admin' }
      );
    } else if (rawRole === 'operations_lead') {
      links.push(
        { label: t('nav.contributions', { defaultValue: 'Cotisations' }), href: '/portal/contributions', route: '/contributions' },
        { label: t('nav.members', { defaultValue: 'Membres' }), href: '/portal/members', route: '/members' },
        { label: t('nav.dashboard', { defaultValue: 'Tableau de bord' }), href: '/portal', route: '/admin' },
        { label: t('nav.allocations', { defaultValue: 'Allocations' }), href: '/portal/assistance', route: '/support' }
      );
    }
    return links.filter((l) => (l.route ? isRouteEnabled(l.route) : true));
  }, [isRouteEnabled, role, t, user?.role, user?.position_role_keys]);

  if (!personalStats) return null;

  const memberFullName = resolveMemberFullDisplayName(personalStats, user, t);

  const currency = (value) => `${Number(value || 0).toLocaleString(localeFmt)} FBU`;
  const currencyMain = (value) => Number(value || 0).toLocaleString(localeFmt);
  const formatTxAmount = (value, isPaid) => {
    const n = Number(value || 0);
    const abs = `${Math.abs(n).toLocaleString(localeFmt)} FBU`;
    if (!isPaid) return `-${abs}`;
    return abs;
  };
  const recentActivity = contributionHistory
    .slice(0, 6)
    .map((item, idx) => {
      const status = String(item.status || '').toLowerCase();
      const isPaid = ['paid', 'approved', 'completed'].includes(status);
      return {
        id: `contrib-${idx}`,
        type: contributionHistoryKindLabel(item.month, isPaid, t),
        description: contributionHistoryDescription(item.month, isPaid, t, localeFmt),
        dateLabel: formatRecentActivityPeriod(item, t, localeFmt),
        amount: Number(item.amount || 0),
      };
    })
    .concat(
      unpaidContributions.slice(0, 2).map((item, idx) => ({
        id: `pending-${idx}`,
        type: t('memberDashboard.pendingRequestType'),
        description: t('memberDashboard.pendingRequestDesc', {
          period: formatRecentActivityPeriod(item, t, localeFmt),
        }),
        dateLabel: formatRecentActivityPeriod(item, t, localeFmt),
        amount: Number(item.amount || 0),
      }))
    );

  /** Navigation thématique (style hub type UNFCU) : regroupe cotisations, allocations, services. */
  const memberHubGroups = [
    {
      id: 'cotisations',
      title: t('memberDashboard.hubContribTitle'),
      sub: t('memberDashboard.hubContribSub'),
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z"
        />
      ),
      links: [
        { label: t('memberPortal.myContributions'), href: '/contributions' },
        ...(showOrgFundOnHome
          ? [{ label: t('memberDashboard.hubLinkLedgerDetailed'), href: '/my-fund/financial' }]
          : [
              { label: t('memberPortal.myOrgFund'), href: '/my-fund' },
              { label: t('memberDashboard.hubLinkLedgerDetailed'), href: '/my-fund/financial' },
            ]),
      ],
    },
    {
      id: 'allocations',
      title: t('memberDashboard.hubAllocTitle'),
      sub: t('memberDashboard.hubAllocSub'),
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      links: [{ label: t('memberDashboard.hubLinkAssistance'), href: '/assistance' }],
    },
    {
      id: 'docs',
      title: t('memberDashboard.hubDocsTitle'),
      sub: t('memberDashboard.hubDocsSub'),
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      ),
      links: [
        { label: t('memberPortal.myDocuments'), href: '/documents' },
        { label: t('nav.calendar'), href: '/calendar' },
      ],
    },
    {
      id: 'aide',
      title: t('memberDashboard.hubHelpTitle'),
      sub: t('memberDashboard.hubHelpSub'),
      icon: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
        />
      ),
      links: [
        { label: t('memberPortal.supportMember'), href: '/support' },
        { label: t('memberPortal.announcements'), href: '/notifications' },
      ],
    },
    ...(managerHubLinks.length > 0
      ? [
          {
            id: 'manager',
            title: t('memberPortal.adminLink'),
            sub: t('memberDashboard.managerHubBlurb', {
              defaultValue: 'Cotisations des membres, approbations et pilotage.',
            }),
            icon: (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d="M11.983 5.346a1 1 0 011.034 0l1.255.76a1 1 0 001.01.03l1.3-.708a1 1 0 011.37.42l.69 1.308a1 1 0 00.79.54l1.49.164a1 1 0 01.88 1.06l-.118 1.475a1 1 0 00.42.92l1.173.922a1 1 0 01.26 1.41l-.847 1.206a1 1 0 00-.12 1.008l.57 1.376a1 1 0 01-.62 1.29l-1.407.49a1 1 0 00-.67.768l-.238 1.48a1 1 0 01-1.18.83l-1.45-.296a1 1 0 00-.96.29l-1.05 1.04a1 1 0 01-1.43 0l-1.05-1.04a1 1 0 00-.96-.29l-1.45.296a1 1 0 01-1.18-.83l-.238-1.48a1 1 0 00-.67-.768l-1.407-.49a1 1 0 01-.62-1.29l.57-1.376a1 1 0 00-.12-1.008l-.847-1.206a1 1 0 01.26-1.41l1.173-.922a1 1 0 00.42-.92l-.118-1.475a1 1 0 01.88-1.06l1.49-.164a1 1 0 00.79-.54l.69-1.308a1 1 0 011.37-.42l1.3.708a1 1 0 001.01-.03l1.255-.76z"
              />
            ),
            links: managerHubLinks.map((l) => ({ label: l.label, href: l.href })),
          },
        ]
      : []),
  ];

  const onboardingSteps = [
    {
      id: 'profile',
      label: t('memberDashboard.onboardingProfile'),
      href: '/profile',
      done: Boolean(personalStats?.member_id),
    },
    {
      id: 'docs',
      label: t('memberDashboard.onboardingDocs'),
      href: '/documents',
      done: false,
    },
    {
      id: 'contrib',
      label: t('memberDashboard.onboardingContrib'),
      href: '/contributions',
      done: contributionHistory.length > 0,
    },
  ];
  const onboardingCompleted = onboardingSteps.filter((step) => step.done).length;
  const onboardingProgress = Math.round((onboardingCompleted / onboardingSteps.length) * 100);
  const nextOnboardingStep = onboardingSteps.find((step) => !step.done);
  const mobileUpcomingActivities = Array.isArray(internalActivities)
    ? internalActivities.slice(0, 3)
    : [];
  const mobileDepositRows = [
    {
      id: 'membership-share',
      title: t('memberDashboard.membershipShareTitle'),
      subtitle: memberAccountSubtitle(personalStats, t),
      amount: Number(personalStats.adhesion_fees || 0),
      accent: 'border-l-[#2f6fd4]',
    },
    {
      id: 'savings',
      title: t('memberDashboard.savingsAccountTitle'),
      subtitle: t('memberDashboard.savingsAccountSubtitle'),
      amount: memberRegularContributionsAmount(personalStats),
      accent: 'border-l-[#1677a1]',
    },
  ];

  return (
    <div className="space-y-3 pb-3 md:space-y-6 md:pb-0">
      {arrearsMonths > 0 ? (
        <Link
          to="/contributions"
          onClick={() =>
            trackDashboardEvent('member_arrears_banner', {
              months: arrearsMonths,
              severity: arrearsCritical ? 'critical' : 'warning',
            })
          }
          className={`mm-press mm-card flex items-center gap-2.5 px-3 py-2.5 transition md:items-start md:gap-3 md:rounded-2xl md:px-4 md:py-3.5 md:shadow-sm ${
            arrearsCritical
              ? 'border border-red-300/80 bg-gradient-to-br from-red-50 to-white md:ring-1 md:ring-red-200/80'
              : 'border border-amber-300/80 bg-gradient-to-br from-amber-50 to-white md:ring-1 md:ring-amber-200/70'
          }`}
        >
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white md:h-10 md:w-10 md:rounded-xl ${
              arrearsCritical ? 'bg-red-600' : 'bg-amber-500'
            }`}
          >
            <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <p className={`text-xs font-bold md:text-sm ${arrearsCritical ? 'text-red-950' : 'text-amber-950'}`}>
              {t('member.arrearsTitle')}
            </p>
            <p
              className={`mt-0.5 text-xs leading-snug md:text-sm ${arrearsCritical ? 'text-red-950/90' : 'text-amber-950/85'}`}
            >
              {t('member.arrearsDetail', { count: arrearsMonths })}
            </p>
          </div>
          <svg
            className={`h-4 w-4 shrink-0 md:mt-1 md:h-5 md:w-5 ${arrearsCritical ? 'text-red-800/75' : 'text-amber-800/70'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      ) : null}

      <section className="mm-shell md:hidden space-y-3">
        {/* Héros compact : salutation + profil */}
        <div className="mm-hero mm-hero--info relative px-3.5 py-3.5 text-[#0f3350]">
          <div className="relative z-[1] flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]/90">
                {t('memberDashboard.welcome')}
              </p>
              <p className="mm-hero__title mt-0.5 truncate text-[1.35rem] text-[#0f3350]">
                {memberFullName}
              </p>
              <p className="truncate text-[11px] font-medium text-slate-600/90">
                {memberAccountSubtitle(personalStats, t)}
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="tabular-nums">
                  {dashboardClock.toLocaleDateString(localeFmt, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                </span>
              </p>
            </div>
            <Link
              to="/profile"
              onClick={() => trackDashboardEvent('member_hero_profile', {})}
              className="mm-press flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-sm font-extrabold text-white shadow-[0_8px_18px_-10px_rgba(47,111,212,0.75)]"
              aria-label={t('nav.profile')}
            >
              {memberFullName.charAt(0).toUpperCase() || 'M'}
            </Link>
          </div>
        </div>

        {/* Statut cotisations : total + détail + CTA unique */}
        <div className="mm-card overflow-hidden">
          <div className="flex items-end justify-between gap-3 px-3.5 pb-3 pt-3.5">
            <div className="min-w-0">
              <p className="font-display text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                {t('memberDashboard.heroTotalContributions')}
              </p>
              <p className="mt-0.5 font-display text-[1.65rem] font-extrabold leading-none tabular-nums tracking-tight text-[#0f3350]">
                {currencyMain(personalStats?.total_contributions)}
                <span className="ml-1 text-xs font-bold text-slate-500">FBU</span>
              </p>
            </div>
            <Link
              to="/contributions"
              onClick={() => trackDashboardEvent('member_hero_total_contrib', {})}
              className="mm-cta min-h-[40px] shrink-0 gap-1 px-3.5 text-xs"
            >
              {t('memberDashboard.payCta')}
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="divide-y divide-slate-100/90 border-t border-slate-100/90">
            {mobileDepositRows.map((row) => (
              <div
                key={row.id}
                className={`flex items-center justify-between gap-2 px-3.5 py-2.5 border-l-[3px] ${row.accent}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-bold tracking-tight text-[#0f3350]">{row.title}</p>
                  <p className="truncate text-[11px] text-slate-500">{row.subtitle}</p>
                </div>
                <p className="shrink-0 font-display text-sm font-extrabold tabular-nums text-[#0f3350]">
                  {currencyMain(row.amount)}
                  <span className="ml-0.5 text-[9px] font-bold text-slate-500">FBU</span>
                </p>
              </div>
            ))}
          </div>
        </div>

        {showOrgFundOnHome ? (
          <Link
            to="/my-fund"
            onClick={() => trackDashboardEvent('member_org_fund_home', { where: 'mobile_balance' })}
            className="mm-card mm-press block overflow-hidden"
          >
            <div className="px-3.5 pb-3 pt-3.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display text-[10px] font-bold uppercase tracking-[0.12em] text-[#1aa6b8]">
                    {t('releve.introKickerMaCaisse')}
                  </p>
                  <p className="mt-0.5 font-display text-[1.45rem] font-extrabold leading-none tabular-nums tracking-tight text-[#0f3350]">
                    {currencyMain(orgFundSnapshot.balance)}
                    <span className="ml-1 text-xs font-bold text-slate-500">FBU</span>
                  </p>
                  <p className="mt-1 truncate text-[11px] text-slate-500">
                    {organizationBrandName || t('releve.defaultOrgName')}
                    {orgFundSnapshot.members > 0
                      ? ` · ${t('releve.membersCount', { count: orgFundSnapshot.members })}`
                      : ''}
                  </p>
                </div>
                <span className="mt-0.5 inline-flex items-center gap-0.5 rounded-full bg-[#edf4ff] px-2 py-1 text-[10px] font-bold text-[#113a5b]">
                  {t('memberDashboard.orgFundOpen')}
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px border-t border-slate-100/90 bg-slate-100/60">
              <div className="bg-white/90 px-3.5 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700/80">
                  {t('releve.cardInTitle')}
                </p>
                <p className="mt-0.5 font-display text-sm font-extrabold tabular-nums text-[#0f3350]">
                  {currencyMain(orgFundSnapshot.income)}
                  <span className="ml-0.5 text-[9px] font-bold text-slate-500">FBU</span>
                </p>
              </div>
              <div className="bg-white/90 px-3.5 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wide text-rose-700/80">
                  {t('releve.cardOutTitle')}
                </p>
                <p className="mt-0.5 font-display text-sm font-extrabold tabular-nums text-[#0f3350]">
                  {currencyMain(orgFundSnapshot.expenses)}
                  <span className="ml-0.5 text-[9px] font-bold text-slate-500">FBU</span>
                </p>
              </div>
            </div>
          </Link>
        ) : null}

        {showAllocationsDashboard ? (
          <details className="mm-card group overflow-hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3.5 py-3 [&::-webkit-details-marker]:hidden">
              <span className="text-sm font-bold tracking-tight text-[#0f3350]">{t('memberDashboard.allocations')}</span>
              <span className="flex items-center gap-1.5">
                <span className="text-sm font-bold tabular-nums text-[#113a5b]">
                  {assistanceLoading ? t('memberDashboard.dash') : `${currencyMain(assistanceStats.amountFinalized)} FBU`}
                </span>
                <svg
                  className="h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-180"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </summary>
            <div className="space-y-1.5 border-t border-slate-100 p-2.5">
              {allocationRowsByType.map((row) => {
                const isOpen = expandedAssistanceKey === row.key;
                return (
                  <div
                    key={row.key}
                    className="overflow-hidden rounded-lg border border-slate-200 border-l-[3px] border-l-amber-500 bg-white"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedAssistanceKey(isOpen ? null : row.key);
                        trackDashboardEvent('dashboard_alloc_row_expand', {
                          row_key: row.key,
                          action: isOpen ? 'close' : 'open',
                        });
                      }}
                      className="w-full px-2.5 py-2 text-left"
                    >
                      <p className="text-sm font-semibold text-[#0f3350]">{row.typeLabel}</p>
                      <p className="mt-0.5 text-[11px] text-slate-600">
                        {t('memberDashboard.amountAllocated')}{' '}
                        <span className="font-semibold tabular-nums text-[#0f3350]">
                          {assistanceLoading ? t('memberDashboard.dash') : currencyMain(row.amount)} FBU
                        </span>
                      </p>
                    </button>
                    {isOpen && row.cases && row.cases.length > 0 && (
                      <div className="border-t border-amber-100/90 bg-amber-50/30 px-2.5 py-2 text-left">
                        <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-slate-800">
                          {row.cases.slice(0, 6).map((c, cidx) => (
                            <li key={String(c.id || c._id || `c-${cidx}`)} className="flex justify-between gap-2">
                              <span className="min-w-0 truncate">
                                {caseStatusLabel(t, c.status)} —{' '}
                                {String(c.id || c.reference || '').slice(0, 12) || t('memberDashboard.dash')}
                              </span>
                              <span className="shrink-0 tabular-nums font-medium">
                                {num(c.paid_amount || c.approved_amount || c.requested_amount).toLocaleString(localeFmt)}{' '}
                                FBU
                              </span>
                            </li>
                          ))}
                        </ul>
                        <Link
                          to="/assistance"
                          onClick={() => trackDashboardEvent('dashboard_assistance_cta', { where: 'mobile_alloc_panel' })}
                          className="mt-1.5 inline-block text-xs font-semibold text-amber-900/90 hover:underline"
                        >
                          {t('memberDashboard.openAssistance')}
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
              {!assistanceLoading && assistanceStats.totalCases === 0 ? (
                <Link
                  to="/assistance"
                  onClick={() => trackDashboardEvent('dashboard_assistance_cta', { where: 'mobile_mes_comptes_empty' })}
                  className="block w-full rounded-lg border border-amber-200/90 bg-amber-50/90 py-2 text-center text-xs font-semibold text-amber-900"
                >
                  {t('memberDashboard.createAllocationRequest')}
                </Link>
              ) : null}
            </div>
          </details>
        ) : null}

        {memberServices.length > 0 || onboardingProgress < 100 ? (
          <details className="mm-card group overflow-hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3.5 py-3 [&::-webkit-details-marker]:hidden">
              <span className="min-w-0">
                <span className="block text-sm font-bold tracking-tight text-[#0f3350]">
                  {t('memberDashboard.moreOnHome')}
                </span>
                <span className="mt-0.5 block text-[11px] font-medium text-slate-500">
                  {t('memberDashboard.moreOnHomeHint', {
                    defaultValue: 'Documents, calendrier et autres services',
                  })}
                </span>
              </span>
              <svg
                className="h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="space-y-2 border-t border-slate-100 px-2.5 pb-2.5 pt-2">
              {memberServices.length > 0 ? (
                <ul className="space-y-1">
                  {memberServices.map((item) => (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => trackDashboardEvent('member_service_tile', { href: item.href })}
                        className="mm-press flex min-h-[48px] items-center justify-between gap-2 rounded-xl border border-slate-100/90 bg-gradient-to-r from-white to-[#f7fbff] px-3 text-sm font-semibold text-[#0f3350]"
                      >
                        <span className="truncate">{t(item.nameKey)}</span>
                        <span className="shrink-0 text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}

              {onboardingProgress < 100 && nextOnboardingStep ? (
                <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-600">
                      {t('memberDashboard.onboardingTitle')} · {onboardingProgress}%
                    </p>
                    <Link
                      to={nextOnboardingStep.href}
                      className="inline-flex min-h-[32px] shrink-0 items-center rounded-lg bg-[#113a5b] px-2.5 text-[11px] font-semibold text-white"
                    >
                      {t('memberDashboard.continue')}
                    </Link>
                  </div>
                  <p className="mt-1 truncate text-[11px] text-slate-500">{nextOnboardingStep.label}</p>
                  <div className="mt-2 h-1 w-full rounded-full bg-slate-200">
                    <div
                      className="h-1 rounded-full bg-[#2f6fd4] transition-all"
                      style={{ width: `${onboardingProgress}%` }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </details>
        ) : null}

        {managerHubLinks.length > 0 ? (
          <UiSurface id="dashboard.member_manager_hub">
            <details className="mm-card group overflow-hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3.5 py-3 [&::-webkit-details-marker]:hidden">
                <span className="text-sm font-bold tracking-tight text-[#0f3350]">{t('memberPortal.adminLink')}</span>
                <svg
                  className="h-4 w-4 shrink-0 text-slate-500 transition-transform group-open:rotate-180"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="border-t border-slate-100 px-2.5 pb-2.5 pt-2">
                <div className="grid grid-cols-2 gap-1.5">
                  {managerHubLinks.map((l) => (
                    <a
                      key={`m-${l.href}-${l.label}`}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackDashboardEvent('member_hub_nav', {
                          group: 'manager',
                          href: l.href,
                          label: l.label,
                          new_window: true,
                        })
                      }
                      className="relative flex min-h-[2.75rem] items-center justify-center rounded-lg border border-slate-200 bg-slate-50/70 px-2 py-1.5 text-center text-[11px] font-semibold leading-snug text-[#113a5b]"
                    >
                      <span className="line-clamp-2 px-1">{l.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </details>
          </UiSurface>
        ) : null}
      </section>

      <section className="hidden md:block entrea-portal-panel">

        <div className="grid gap-4 p-6 md:grid-cols-[minmax(248px,300px)_1fr] md:items-stretch">
          <div className="entrea-portal-inset entrea-portal-inset--muted flex min-h-[220px] w-full flex-col items-center justify-center px-5 py-8 text-center md:min-h-0 md:h-full">
            <div className="mb-4 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-100/90 ring-1 ring-sky-200/60">
              <svg className="h-7 w-7 text-entrea-royal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M3 10h18M7 15h1m3 0h6M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
                />
              </svg>
            </div>
            <p className="text-lg font-semibold text-entrea-ink">{t('memberDashboard.mesDepots')}</p>
            <p className="mt-3 text-4xl font-bold leading-none tracking-tight text-entrea-ink">
              {currency(personalStats.total_contributions)}
            </p>
            <p className="mt-2 max-w-[16rem] text-base leading-snug text-slate-600">
              {t('memberDashboard.cotisationsPlusAdhesion')}
            </p>
          </div>

          <div className="flex min-h-0 flex-col justify-center gap-2.5">
            {[
              {
                id: 'adhesion',
                title: t('memberDashboard.titleAdhesionFees'),
                subtitle: t('memberDashboard.adhesionEntryFee'),
                status: Number(personalStats.adhesion_fees || 0) > 0
                  ? t('memberDashboard.statusUpToDate')
                  : t('memberDashboard.statusPending'),
                amount: personalStats.adhesion_fees || 0,
              },
              {
                id: 'cotisations',
                title: t('nav.contributions'),
                subtitle: memberAccountSubtitle(personalStats, t),
                status:
                  arrearsMonths > 0
                    ? t('memberDashboard.statusLate', { count: arrearsMonths })
                    : t('memberDashboard.statusUpToDate'),
                amount: memberRegularContributionsAmount(personalStats),
              },
            ].map((row) => {
              const isOpen = expandedAccountRow === row.id;
              const panelId = `entrea-deposit-panel-${row.id}`;
              const triggerId = `entrea-deposit-trigger-${row.id}`;
              const recentTxList =
                row.id === 'adhesion' ? recentAdhesionTransactions : recentRegularTransactions;
              return (
                <div key={row.id} className="overflow-hidden rounded-md">
                  <div
                    id={triggerId}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    aria-label={
                      isOpen
                        ? t('memberDashboard.collapseDetailAria', { title: row.title })
                        : t('memberDashboard.expandDetailAria', { title: row.title })
                    }
                    onClick={() => {
                      if (isOpen) {
                        trackDashboardEvent('dashboard_deposit_row_expand', { row_id: row.id, action: 'close' });
                        setExpandedAccountRow(null);
                      } else {
                        trackDashboardEvent('dashboard_deposit_row_expand', { row_id: row.id, action: 'open' });
                        setExpandedAccountRow(row.id);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (isOpen) {
                          trackDashboardEvent('dashboard_deposit_row_expand', { row_id: row.id, action: 'close' });
                          setExpandedAccountRow(null);
                        } else {
                          trackDashboardEvent('dashboard_deposit_row_expand', { row_id: row.id, action: 'open' });
                          setExpandedAccountRow(row.id);
                        }
                      }
                    }}
                    className="entrea-account-row grid min-h-[4.75rem] cursor-pointer grid-cols-[1fr_minmax(4.5rem,auto)_minmax(5.5rem,auto)] items-center gap-x-3 gap-y-1 px-4 py-4 transition hover:bg-slate-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-entrea-royal focus-visible:ring-offset-2 sm:grid-cols-[1fr_120px_152px]"
                  >
                    <div className="min-w-0 self-center text-left">
                      <p className="text-lg font-semibold text-slate-900">{row.title}</p>
                      <p className="mt-0.5 break-words text-base text-slate-600">{row.subtitle}</p>
                    </div>
                    <p className="self-center text-right text-lg text-slate-600">{row.status}</p>
                    <p className="self-center text-right text-lg font-bold tabular-nums text-slate-900">
                      {currency(row.amount)}
                    </p>
                  </div>
                  {isOpen && (
                    <div
                      id={panelId}
                      className="mt-2 rounded-sm border border-[#e0e0e0] bg-white shadow-sm"
                      role="region"
                      aria-labelledby={triggerId}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x md:divide-[#e0e0e0]">
                        <div className="px-5 py-4">
                          {row.id === 'adhesion' ? (
                            <>
                              <h3 className="text-lg font-bold tracking-tight text-[#003366]">
                                {t('memberDashboard.detailAdhesionTitle')}
                              </h3>
                              <div className="mt-3 h-px w-full bg-[#e0e0e0]" />
                              <div className="mt-4 space-y-2 text-base text-[#666666]">
                                <p>
                                  <span className="font-semibold text-[#003366]">{t('memberDashboard.memberNumber')}</span>{' '}
                                  {personalStats.member_id || t('memberDashboard.dash')}
                                </p>

                                <p>
                                  <span className="font-semibold text-[#003366]">
                                    {t('memberDashboard.balanceCurrentTitleCase')}
                                  </span>{' '}
                                  {currency(personalStats.adhesion_fees || 0)}
                                </p>

                              </div>
                            </>
                          ) : (
                            <>
                              <h3 className="text-lg font-bold tracking-tight text-[#003366]">
                                {t('memberDashboard.detailCotisationsTitle')}
                              </h3>
                              <div className="mt-3 h-px w-full bg-[#e0e0e0]" />
                              <div className="mt-4 space-y-2 text-base text-[#666666]">
                                <p>
                                  <span className="font-semibold text-[#003366]">{t('memberDashboard.balanceCurrent')}</span>{' '}
                                  {currency(memberRegularContributionsAmount(personalStats))}
                                </p>
                                <p>
                                  <span className="font-semibold text-[#003366]">{t('memberDashboard.memberNumber')}</span>{' '}
                                  {personalStats.member_id || t('memberDashboard.dash')}
                                </p>
                                <p>
                                  <span className="font-semibold text-[#003366]">{t('memberDashboard.lastPayment')}</span>{' '}
                                  {cotisationCalMeta.dernierPaiement}
                                </p>
                                <p>
                                  <span className="font-semibold text-[#003366]">{t('memberDashboard.nextPayment')}</span>{' '}
                                  {cotisationCalMeta.prochainPaiement}
                                </p>
                              </div>
                            </>
                          )}
                          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
                            <Link to="/contributions" className="text-[#0056b3] hover:underline">
                              {t('memberDashboard.seeMyContributions')}
                            </Link>
                            <Link to="/my-fund" className="text-[#0056b3] hover:underline">
                              {t('memberPortal.myOrgFund')}
                            </Link>
                          </div>
                        </div>

                        <div className="flex min-h-0 flex-col border-t border-[#e0e0e0] md:border-t-0">
                          <div className="px-5 pt-4 pb-3">
                            <h3 className="text-lg font-bold tracking-tight text-[#003366]">
                              {t('memberDashboard.recentTransactions')}
                            </h3>
                            <div className="mt-3 h-px w-full bg-[#e0e0e0]" />
                          </div>

                          {recentTxList.length === 0 ? (
                            <div className="px-5 py-6 text-base text-[#666666]">
                              {row.id === 'adhesion'
                                ? t('memberDashboard.noRecentAdhesion')
                                : t('memberDashboard.noRecentMonthly')}
                            </div>
                          ) : (
                            <ul className="divide-y divide-[#e0e0e0]">
                              {recentTxList.map((tx) => (
                                <li key={tx.id} className="px-5 py-4">
                                  <div className="flex items-start justify-between gap-3">
                                    <p className="min-w-0 flex-1 text-sm font-semibold leading-snug text-[#003366]">
                                      {tx.depotLine}
                                    </p>
                                    <span
                                      className={
                                        tx.isPaid
                                          ? 'shrink-0 text-base font-bold tabular-nums text-emerald-600'
                                          : 'shrink-0 text-base font-bold tabular-nums text-slate-600'
                                      }
                                    >
                                      {formatTxAmount(tx.amount, tx.isPaid)}
                                    </span>
                                  </div>
                                  <p className="mt-2 text-sm leading-relaxed text-[#666666]">{tx.metaLine}</p>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div className="mt-auto flex justify-end px-5 pb-2 pt-1">
                            <Link
                              to="/contributions"
                              className="inline-flex items-center gap-2 text-base font-bold text-[#0056b3] hover:text-[#004494]"
                            >
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0056b3] text-white shadow-sm">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden
                                >
                                  <path d="M9 6l6 6-6 6" />
                                </svg>
                              </span>
                              {t('memberDashboard.seeAll')}
                            </Link>
                          </div>
                        </div>
                      </div>

                      <div className="h-px w-full bg-[#e0e0e0]" />

                      <div className="flex justify-end px-5 py-3">
                        <button
                          type="button"
                          onClick={() => setExpandedAccountRow(null)}
                          className="inline-flex items-center gap-2 text-base font-normal text-[#0056b3] hover:text-[#004494]"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            aria-hidden
                          >
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                          {t('memberDashboard.close')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {showOrgFundOnHome ? (
        <section className="hidden md:block entrea-portal-panel">
          <div className="grid gap-4 p-6 md:grid-cols-[minmax(248px,300px)_1fr] md:items-stretch">
            <div className="entrea-portal-inset flex min-h-[220px] w-full flex-col items-center justify-center bg-gradient-to-br from-[#edf4ff] to-white px-5 py-8 text-center ring-1 ring-[#bcd4f6]/60 md:min-h-0 md:h-full">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2f6fd4]">
                {t('releve.introKickerMaCaisse')}
              </p>
              <p className="mt-2 text-lg font-semibold text-entrea-ink">
                {organizationBrandName || t('releve.defaultOrgName')}
              </p>
              <p className="mt-3 text-4xl font-bold leading-none tracking-tight text-entrea-ink">
                {currency(orgFundSnapshot.balance)}
              </p>
              <p className="mt-2 max-w-[16rem] text-base leading-snug text-slate-600">
                {t('releve.cardBalanceSubtitle')}
              </p>
              {orgFundSnapshot.members > 0 ? (
                <p className="mt-1.5 text-sm text-slate-600">
                  {t('releve.membersCount', { count: orgFundSnapshot.members })}
                  {orgFundSnapshot.activeMembers > 0
                    ? ` · ${t('releve.activeMembersSuffix', { count: orgFundSnapshot.activeMembers })}`
                    : ''}
                </p>
              ) : null}
              <div className="mt-4 flex w-full max-w-[16rem] flex-col gap-2">
                <Link
                  to="/my-fund"
                  onClick={() => trackDashboardEvent('member_org_fund_home', { where: 'desktop_open' })}
                  className="mm-cta w-full text-sm"
                >
                  {t('memberDashboard.orgFundOpen')}
                </Link>
                <Link
                  to="/my-fund/financial"
                  onClick={() => trackDashboardEvent('member_org_fund_home', { where: 'desktop_movements' })}
                  className="mm-cta mm-cta--ghost w-full text-sm"
                >
                  {t('releve.detailCta')}
                </Link>
              </div>
            </div>

            <div className="flex min-h-0 flex-col justify-center gap-2.5">
              {[
                {
                  id: 'in',
                  title: t('releve.cardInTitle'),
                  subtitle: t('releve.cardInSubtitle'),
                  amount: orgFundSnapshot.income,
                  accent: 'border-l-emerald-500',
                },
                {
                  id: 'out',
                  title: t('releve.cardOutTitle'),
                  subtitle: t('releve.cardOutSubtitle'),
                  amount: orgFundSnapshot.expenses,
                  accent: 'border-l-rose-500',
                },
                {
                  id: 'contrib',
                  title: t('releve.cardContribTitle'),
                  subtitle: t('releve.cardContribSubtitle'),
                  amount: orgFundSnapshot.contributions,
                  accent: 'border-l-amber-500',
                },
              ].map((row) => (
                <div
                  key={row.id}
                  className={`entrea-account-row grid min-h-[4.75rem] grid-cols-[1fr_minmax(5.5rem,auto)] items-center gap-x-3 border-l-[3px] px-4 py-4 sm:grid-cols-[1fr_152px] ${row.accent}`}
                >
                  <div className="min-w-0 self-center text-left">
                    <p className="text-lg font-semibold text-slate-900">{row.title}</p>
                    <p className="mt-0.5 break-words text-base text-slate-600">{row.subtitle}</p>
                  </div>
                  <p className="self-center text-right text-lg font-bold tabular-nums text-slate-900">
                    {currency(row.amount)}
                  </p>
                </div>
              ))}
              {orgFundSnapshot.pendingContributions > 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
                  <span className="font-semibold">{t('releve.pendingContributions')}</span>{' '}
                  {currency(orgFundSnapshot.pendingContributions)}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {showAllocationsDashboard ? (
      <section className="hidden md:block entrea-portal-panel">
        <div className="grid gap-4 p-6 md:grid-cols-[minmax(248px,300px)_1fr] md:items-stretch">
          <div className="entrea-portal-inset entrea-portal-inset--warn flex min-h-[220px] w-full flex-col items-center justify-center px-5 py-8 text-center md:min-h-0 md:h-full">
            <div className="mb-4 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100/90 ring-1 ring-amber-200/60">
              <svg
                className="h-7 w-7 text-amber-800"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.75}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-lg font-semibold text-entrea-ink">{t('memberDashboard.mesAllocations')}</p>
            <p className="mt-3 text-4xl font-bold leading-none tracking-tight text-entrea-ink">
              {assistanceLoading ? t('memberDashboard.dash') : currency(assistanceStats.amountFinalized)}
            </p>
            <p className="mt-2 max-w-[16rem] text-base leading-snug text-slate-600">
              {t('memberDashboard.allocationsTotalPaid')}
            </p>
            <p className="mt-1.5 max-w-[16rem] text-sm leading-snug text-slate-600">
              {assistanceDossierSummary}
            </p>
            {!assistanceLoading && assistanceStats.totalCases === 0 ? (
              <Link
                to="/assistance"
                onClick={() => trackDashboardEvent('dashboard_assistance_cta', { where: 'desktop_summary_empty' })}
                className="mt-4 inline-flex min-h-[44px] w-full max-w-[16rem] items-center justify-center rounded-lg bg-amber-100/90 px-3 text-sm font-semibold text-amber-900 ring-1 ring-amber-200/80 transition hover:bg-amber-100"
              >
                {t('memberDashboard.createAllocationRequest')}
              </Link>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-col justify-center gap-2.5">
            {allocationRowsByType.map((row) => {
              const isRowOpen = expandedAssistanceKey === row.key;
              return (
                <div key={row.key} className="overflow-hidden rounded-md">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (isRowOpen) {
                        setExpandedAssistanceKey(null);
                        trackDashboardEvent('dashboard_alloc_row_expand', { row_key: row.key, where: 'desktop', action: 'close' });
                      } else {
                        setExpandedAssistanceKey(row.key);
                        trackDashboardEvent('dashboard_alloc_row_expand', { row_key: row.key, where: 'desktop', action: 'open' });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedAssistanceKey(isRowOpen ? null : row.key);
                      }
                    }}
                    className="entrea-demands-row grid min-h-[5rem] cursor-pointer grid-cols-1 items-start gap-y-2 px-4 py-4 transition hover:bg-amber-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 sm:grid-cols-[1fr_minmax(7rem,auto)] sm:items-center sm:gap-x-3"
                  >
                    <div className="min-w-0 self-center text-left">
                      <p className="text-lg font-semibold text-entrea-ink">{row.typeLabel}</p>
                      <p className="mt-0.5 text-sm text-slate-600">{t('memberDashboard.allocationTypeLabel')}</p>
                    </div>
                    <div className="self-center text-right sm:pt-0">
                      <p className="text-lg font-bold tabular-nums text-slate-900">
                        {assistanceLoading ? t('memberDashboard.dash') : currency(row.amount)}
                      </p>
                      <p className="text-xs text-slate-600">{t('memberDashboard.allocatedAmountShort')}</p>
                    </div>
                  </div>
                  {isRowOpen && row.cases && row.cases.length > 0 && (
                    <div className="border-t border-amber-100/90 bg-amber-50/25 px-4 py-3">
                      <h4 className="text-sm font-bold tracking-tight text-[#4a2c00]">
                        {t('memberDashboard.dossiersLinked')}
                      </h4>
                      <ul className="mt-2 space-y-2 text-sm text-slate-800">
                        {row.cases.slice(0, 10).map((c, cidx) => (
                          <li
                            key={String(c.id || c._id || `dc-${cidx}`)}
                            className="flex flex-wrap items-baseline justify-between gap-2 border-b border-amber-100/80 pb-2 last:border-0 last:pb-0"
                          >
                            <span className="min-w-0">
                              <span className="font-semibold text-slate-900">{caseStatusLabel(t, c.status)}</span>
                              {c.id || c._id ? (
                                <span className="ml-1 text-slate-500">— {String(c.id || c._id).slice(0, 14)}</span>
                              ) : null}
                            </span>
                            <span className="shrink-0 tabular-nums font-medium text-entrea-ink">
                              {num(c.paid_amount || c.approved_amount || c.requested_amount).toLocaleString(localeFmt)} FBU
                            </span>
                          </li>
                        ))}
                      </ul>
                      {row.cases.length > 10 ? (
                        <p className="mt-2 text-xs text-slate-600">
                          {t('memberDashboard.allocationMoreDesktop', { count: row.cases.length - 10 })}
                        </p>
                      ) : null}
                      <div className="mt-3">
                        <Link
                          to="/assistance"
                          onClick={() => trackDashboardEvent('dashboard_assistance_cta', { where: 'desktop_alloc_panel' })}
                          className="text-sm font-semibold text-[#0056b3] hover:underline"
                        >
                          {t('memberDashboard.openAssistance')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      ) : null}

      <section className="hidden md:block rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold tracking-tight text-[#0f3350]">
          {t('memberDashboard.otherServicesTitle')}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('memberDashboard.otherServicesBlurb')}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {memberHubGroups.map((g) => (
            <article
              key={g.id}
              className={`flex flex-col rounded-xl border p-5 transition ${
                g.id === 'manager'
                  ? 'border-[#bcd4f6] bg-gradient-to-br from-[#edf4ff]/90 to-white ring-1 ring-[#2f6fd4]/10'
                  : 'border-slate-200/90 bg-slate-50/50 hover:border-[#bcd4f6] hover:bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${
                    g.id === 'manager'
                      ? 'border-[#bcd4f6] bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                      : 'border-slate-200 bg-white text-[#113a5b]'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    {g.icon}
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-[#0f3350]">{g.title}</h3>
                  <p className="mt-0.5 text-sm text-slate-600">{g.sub}</p>
                </div>
              </div>
              <ul className="mt-4 space-y-0.5 border-t border-slate-200/80 pt-3">
                {(g.id === 'manager' && g.links.length > MANAGER_HUB_LINKS_VISIBLE
                  ? managerLinksExpanded
                    ? g.links
                    : g.links.slice(0, MANAGER_HUB_LINKS_VISIBLE)
                  : g.links
                ).map((l) => (
                  <li key={`${g.id}-${l.label}`}>
                    {g.id === 'manager' ? (
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          trackDashboardEvent('member_hub_nav', {
                            group: g.id,
                            href: l.href,
                            label: l.label,
                            new_window: true,
                          })
                        }
                        className="flex min-h-[40px] items-center justify-between rounded-lg px-1 text-[15px] font-semibold text-[#113a5b] hover:bg-[#edf4ff]/80"
                      >
                        {l.label}
                        <span className="shrink-0 pl-2 text-slate-400" aria-hidden>
                          ↗
                        </span>
                      </a>
                    ) : (
                      <Link
                        to={l.href}
                        onClick={() =>
                          trackDashboardEvent('member_hub_nav', { group: g.id, href: l.href, label: l.label })
                        }
                        className="flex min-h-[40px] items-center justify-between rounded-lg px-1 text-[15px] font-medium text-[#113a5b] hover:bg-white/90"
                      >
                        {l.label}
                        <span className="shrink-0 pl-2 text-slate-400" aria-hidden>
                          ›
                        </span>
                      </Link>
                    )}
                  </li>
                ))}
                {g.id === 'manager' && g.links.length > MANAGER_HUB_LINKS_VISIBLE ? (
                  <li key={`${g.id}-toggle-more`}>
                    <button
                      type="button"
                      onClick={() => setManagerLinksExpanded((v) => !v)}
                      className="flex min-h-[40px] w-full items-center justify-between rounded-lg px-1 text-[15px] font-medium text-slate-600 hover:bg-[#edf4ff]/60"
                    >
                      {managerLinksExpanded ? t('memberPortal.showLess') : t('memberPortal.more')}
                      <span className="shrink-0 pl-2 text-slate-400" aria-hidden>
                        {managerLinksExpanded ? '↑' : '⋯'}
                      </span>
                    </button>
                  </li>
                ) : null}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="hidden md:block entrea-portal-panel">
        <div className="entrea-portal-panel__head px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">
              {t('memberDashboard.recentActivityTitle')}
            </h2>
            <Link to="/my-fund/financial" className="text-lg font-semibold text-entrea-royal hover:text-entrea-ink">
              {t('memberDashboard.seeAllActivity')}
            </Link>
          </div>
        </div>
        <div className="px-6 py-4">
          {recentActivity.length === 0 ? (
            <p className="text-lg text-slate-600">{t('memberDashboard.noRecentActivity')}</p>
          ) : (
            <div className="entrea-portal-divide">
              {recentActivity.map((item) => (
                <div key={item.id} className="grid grid-cols-[1.2fr_1fr_132px] gap-3 py-3.5">
                  <div>
                    <p className="text-lg font-semibold text-slate-900">{item.type}</p>
                    <p className="text-base text-slate-600">{item.description}</p>
                  </div>
                  <div className="flex items-center text-base text-slate-600">
                    {item.dateLabel || t('memberDashboard.dash')}
                  </div>
                  <div className="flex items-center justify-end text-lg font-semibold text-slate-900">
                    {currency(item.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="hidden md:block entrea-portal-trust">
        <div className="entrea-portal-trust__head px-4 py-2.5 text-center text-lg font-medium">
          {t('memberDashboard.trustHead')}
        </div>
        <div className="grid gap-2 px-4 py-3.5 text-base text-slate-200 sm:grid-cols-2">
          <div>
            <p className="font-semibold text-white">{t('memberDashboard.trustBrand')}</p>
            <p>{t('memberDashboard.trustBlurb')}</p>
          </div>
          <div className="text-left sm:text-right">
            <p>{t('memberDashboard.trustSupport')}</p>
            <p>
              {t('memberDashboard.trustMemberRef')}{' '}
              {String(personalStats.member_number || '').trim() ||
                (personalStats.member_id ? String(personalStats.member_id) : t('memberDashboard.dash'))}
            </p>
            {String(personalStats.member_number || '').trim() && personalStats.member_id ? (
              <p className="mt-1 text-sm text-slate-200">
                {t('memberDashboard.trustTechId', { id: String(personalStats.member_id) })}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MemberDashboard;
